import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  CheckCircleOutlined,
  CloseOutlined,
  UploadOutlined,
  CheckOutlined,
  SbButton,
  SbUpload,
  SbModal,
  SbRadio,
  SbTable,
} from 'uiCore';
import {
  Step2WrapperStyled,
  ReviewScrollableStyled,
  SectionBlockStyled,
  EmptyStateTextStyled,
  MatchedFileChipStyled,
  CandidateListStyled,
  CandidateDividerStyled,
  CandidateOptionStyled,
  CandidateFilenameStyled,
  ModalBodyTextStyled,
} from './SbZipMappingReview.styles';

const NONE_VALUE = '__none__';

// ── Main component ────────────────────────────────────────────────────────────
const SbZipMappingReview = forwardRef(({
  rows,
  hasZip,
  zipError,
  autoMapped,
  needsReview,
  onZipUpload,
  onZipRemove,
  onConfirm,
  zipFilename,
  rowIndexMap,
  locked = false,
  onPendingCountChange,
  onCollapsedStateChange,
}, ref) => {
  const [decisions, setDecisions]              = useState({});
  const [removedAutoKeys, setRemovedAutoKeys]  = useState({});
  const [pendingSelection, setPendingSelection] = useState(null);

  useEffect(() => {
    setDecisions({});
    setRemovedAutoKeys({});
  }, [autoMapped, needsReview]);

  const handleSelect = useCallback((rowKey, value) => {
    setDecisions((prev) => ({ ...prev, [rowKey]: value === NONE_VALUE ? null : value }));
  }, []);

  const handleDirectUpload = useCallback((rowKey, file) => {
    setDecisions((prev) => ({ ...prev, [rowKey]: file }));
  }, []);

  const handleCandidateSelect = useCallback((rowKey, filename) => {
    setPendingSelection({ rowKey, filename });
  }, []);

  const handleRemoveAutoMap = useCallback((rowKey, originalFilename) => {
    setRemovedAutoKeys((prev) => ({ ...prev, [rowKey]: originalFilename }));
  }, []);

  const handleRemoveDecision = useCallback((rowKey) => {
    setDecisions((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);

  const buildDecisions = useCallback(() => {
    const allDecisions = { ...decisions };
    autoMapped.forEach(({ rowKey, filename }) => {
      if (!(rowKey in removedAutoKeys)) allDecisions[rowKey] = filename;
    });
    return allDecisions;
  }, [decisions, autoMapped, removedAutoKeys]);

  const rowByKey = Object.fromEntries(rows.map((r, i) => [r.key, { ...r, _rowNumber: rowIndexMap[r.key] ?? i }]));

  // Build a unified flat list of all rows with their state
  const allRowEntries = rows.map((r) => {
    const rowKey = r.key;
    const autoMatch = autoMapped.find((m) => m.rowKey === rowKey);
    const needsReviewEntry = needsReview.find((n) => n.rowKey === rowKey);
    const isAutoRemoved = rowKey in removedAutoKeys;
    const hasDecision = rowKey in decisions;

    let status = 'generate'; // generate from metadata (no zip)
    let matchedFile = null;
    let candidates = [];
    let canRemove = false;
    let onRemove = null;

    if (autoMatch && !isAutoRemoved) {
      status = 'mapped';
      matchedFile = autoMatch.filename;
      canRemove = true;
      onRemove = () => handleRemoveAutoMap(rowKey, autoMatch.filename);
    } else if (hasDecision) {
      const d = decisions[rowKey];
      status = d === null ? 'skipped' : 'mapped';
      matchedFile = d;
      canRemove = true;
      onRemove = () => handleRemoveDecision(rowKey);
    } else if (needsReviewEntry || isAutoRemoved) {
      status = 'pending';
      candidates = isAutoRemoved
        ? [{ filename: removedAutoKeys[rowKey] }]
        : (needsReviewEntry?.candidates ?? []);
    }

    return { rowKey, row: { ...r, _rowNumber: rowIndexMap[r.key] ?? rows.indexOf(r) }, status, matchedFile, candidates, canRemove, onRemove };
  });

  const pendingCount = allRowEntries.filter((e) => e.status === 'pending').length;

  useEffect(() => {
    onPendingCountChange?.(pendingCount);
  }, [pendingCount, onPendingCountChange]);

  useEffect(() => {
    onCollapsedStateChange?.({ isAllCollapsed: false, isAllExpanded: true });
  }, [onCollapsedStateChange]);

  useImperativeHandle(ref, () => ({
    collapseAll: () => {},
    expandAll: () => {},
    getPendingCount: () => pendingCount,
    buildDecisions,
  }));

  const columns = [
    {
      title: '#',
      key: 'rowNumber',
      width: 100,
      align: 'center',
      render: (_, record) => record.row._rowNumber + 1,
    },
    {
      title: 'Job Code & Family',
      key: 'jobCode',
      dataIndex: ['row', 'jobCode'],
      width: '24%',
      render: (_, record) => record.row.jobCode || '—',
    },
    {
      title: 'Job Title',
      key: 'jobTitle',
      dataIndex: ['row', 'jobTitle'],
      width: '18%',
      render: (_, record) => record.row.jobTitle || '—',
    },
    {
      title: 'Matches (Choose one / upload)',
      key: 'matches',
      render: (_, record) => {
        const { rowKey, status, matchedFile, candidates, onRemove } = record;
        if (status === 'mapped' && matchedFile) {
          return (
            <MatchedFileChipStyled>
              <CheckOutlined />
              {typeof matchedFile === 'string' ? matchedFile.split('/').pop() : matchedFile.name}
              {!locked && onRemove && (
                <SbButton
                  buttonType="action"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onRemove}
                  style={{ marginLeft: 4 }}
                />
              )}
            </MatchedFileChipStyled>
          );
        }
        if (status === 'skipped') {
          return <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Generate from metadata</span>;
        }
        if (status === 'pending' && candidates.length > 0) {
          if (locked) {
            return <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Generate from metadata</span>;
          }
          return (
            <CandidateListStyled>
              {candidates.slice(0, 5).map(({ filename }, idx) => (
                <React.Fragment key={filename}>
                  {idx > 0 && <CandidateDividerStyled>|</CandidateDividerStyled>}
                  <CandidateOptionStyled $selected={false} onClick={() => handleCandidateSelect(rowKey, filename)}>
                    <SbRadio value={filename} />
                    <CandidateFilenameStyled>{filename.split('/').pop()}</CandidateFilenameStyled>
                  </CandidateOptionStyled>
                </React.Fragment>
              ))}
            </CandidateListStyled>
          );
        }
        if (status === 'pending') {
          return <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Generate from metadata</span>;
        }
        return <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Generate from metadata</span>;
      },
    },
    {
      title: 'Upload File',
      key: 'upload',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        if (locked) return null;
        return (
          <SbUpload
            accept=".pdf,.docx,.doc,.txt"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => { handleDirectUpload(record.rowKey, file); return false; }}
          >
            <SbButton buttonType="secondary" size="small" icon={<UploadOutlined />} />
          </SbUpload>
        );
      },
    },
    {
      title: 'Skip File Reference',
      key: 'skip',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        const { rowKey, status } = record;
        if (status === 'pending' && !locked) {
          return (
            <SbButton buttonType="secondary" size="small" onClick={() => handleCandidateSelect(rowKey, NONE_VALUE)}>
              Skip
            </SbButton>
          );
        }
        if (status === 'mapped') {
          return <span style={{ color: '#9CA3AF', fontSize: 14 }}>—</span>;
        }
        if (status === 'skipped') {
          return <CheckOutlined style={{ color: '#059669', fontSize: 14 }} />;
        }
        return null;
      },
    },
  ];

  if (!rows.length) return null;

  const showTable = hasZip || locked;

  return (
    <Step2WrapperStyled>

      {/* ── Table ── */}
      <ReviewScrollableStyled>
        <SectionBlockStyled>
          {showTable ? (
            <SbTable
              columns={columns}
              dataSource={allRowEntries}
              rowKey="rowKey"
              pagination={false}
              size="middle"
              scroll={{ y: 'calc(100vh - 280px)', x: 'max-content' }}
            />
          ) : (
            <EmptyStateTextStyled>
              Upload a ZIP file above to match reference files to your job titles.
            </EmptyStateTextStyled>
          )}
        </SectionBlockStyled>
      </ReviewScrollableStyled>

      <SbModal
        open={!!pendingSelection}
        title={pendingSelection?.filename === NONE_VALUE ? 'Confirm skip' : 'Confirm file mapping'}
        okText={pendingSelection?.filename === NONE_VALUE ? 'Yes, skip' : 'Map this file'}
        cancelText="Cancel"
        onOk={() => {
          handleSelect(pendingSelection.rowKey, pendingSelection.filename);
          setPendingSelection(null);
        }}
        onCancel={() => setPendingSelection(null)}
      >
        {pendingSelection?.filename === NONE_VALUE ? (
          <ModalBodyTextStyled>
            <strong>{rowByKey[pendingSelection?.rowKey]?.jobTitle || 'This role'}</strong> will be generated from metadata only — no reference file will be attached.
          </ModalBodyTextStyled>
        ) : (
          <ModalBodyTextStyled>
            Map <strong>{pendingSelection?.filename?.split('/').pop()}</strong> to{' '}
            <strong>{rowByKey[pendingSelection?.rowKey]?.jobTitle || 'this role'}</strong>?
          </ModalBodyTextStyled>
        )}
      </SbModal>

    </Step2WrapperStyled>
  );
});

export default SbZipMappingReview;
